import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Download,
  Search,
  Filter,
  Plus,
  Calendar,
  User,
  CheckCircle,
  Clock,
  XCircle,
  FileCheck,
  Eye,
  Edit,
  Send,
  FileSignature,
  Link as LinkIcon,
  Copy,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractsListPage = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, number
  
  // حالة modal روابط المشاركة
  const [shareLinksModal, setShareLinksModal] = useState(false);
  const [currentContractLinks, setCurrentContractLinks] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  
  // حالة نسخ رابط التوقيع
  const [copiedSignatureLink, setCopiedSignatureLink] = useState(null);

  useEffect(() => {
    fetchContracts();
    fetchStats();
  }, [statusFilter]);

  const fetchContracts = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${BACKEND_URL}/api/contracts/list?limit=100`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContracts(response.data.contracts || []);
    } catch (err) {
      console.error('خطأ في جلب العقود:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/contracts/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('خطأ في جلب الإحصائيات:', err);
    }
  };

  const downloadPDF = async (contractId, language, contractNumber) => {
    try {
      const token = localStorage.getItem('token');
      console.log(`🔽 بدء تحميل PDF: ${contractNumber} - اللغة: ${language}`);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/download/${contractId}/${language}`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Accept': 'application/pdf'
          },
          responseType: 'blob',
          timeout: 30000 // 30 seconds timeout
        }
      );

      console.log('✅ تم استلام الملف من الخادم');
      console.log('حجم الملف:', response.data.size, 'bytes');
      console.log('نوع الملف:', response.data.type);

      // إنشاء blob وتحميله
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${contractNumber}_${language.toUpperCase()}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // تنظيف
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('✅ اكتمل التحميل بنجاح');
      }, 100);
      
    } catch (err) {
      console.error('❌ خطأ في تحميل PDF:', err);
      console.error('التفاصيل:', err.response?.data || err.message);
      
      let errorMessage = 'حدث خطأ في تحميل الملف';
      if (err.response?.status === 404) {
        errorMessage = 'ملف PDF غير موجود';
      } else if (err.response?.status === 403) {
        errorMessage = 'ليس لديك صلاحية تحميل هذا الملف';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'انتهت مهلة التحميل. يرجى المحاولة مرة أخرى';
      }
      
      alert(errorMessage);
    }
  };

  const sendSignatureLink = async (contractId, clientId, contractNumber) => {
    if (!confirm(`هل تريد إرسال رابط التوقيع للعميل عبر البريد الإلكتروني؟`)) {
      return;
    }

    setSendingEmail(contractId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/notifications/send-signature-link`,
        {
          contract_id: contractId,
          recipient_user_id: clientId,
          language: 'arabic'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            contract_id: contractId,
            recipient_user_id: clientId,
            language: 'arabic'
          }
        }
      );

      if (response.data.email_status?.success) {
        alert(`✅ تم إرسال رابط التوقيع بنجاح إلى:\n${response.data.recipient.email}`);
      } else {
        alert(`⚠️ لم يتم إرسال البريد:\n${response.data.email_status?.message || 'خطأ غير معروف'}`);
      }
    } catch (err) {
      console.error('خطأ في إرسال البريد:', err);
      alert('حدث خطأ في إرسال رابط التوقيع');
    } finally {
      setSendingEmail(null);
    }
  };

  // الحصول على روابط المشاركة العامة
  const getShareLinks = async (contractId) => {
    setLoadingLinks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/${contractId}/share-links`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setCurrentContractLinks(response.data);
      setShareLinksModal(true);
    } catch (err) {
      console.error('خطأ في جلب الروابط:', err);
      alert('حدث خطأ في جلب روابط المشاركة');
    } finally {
      setLoadingLinks(false);
    }
  };

  // نسخ الرابط
  const copyToClipboard = async (text, linkType) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(linkType);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('خطأ في النسخ:', err);
      alert('حدث خطأ في نسخ الرابط');
    }
  };

  // نسخ رابط التوقيع للعميل
  const copySignatureLinkForClient = async (contractId) => {
    try {
      // توليد رابط التوقيع العام (بدون تسجيل دخول)
      const signatureLink = `${BACKEND_URL.replace('/api', '')}/sign/${contractId}`;
      
      // نسخ الرابط
      await navigator.clipboard.writeText(signatureLink);
      setCopiedSignatureLink(contractId);
      setTimeout(() => setCopiedSignatureLink(null), 2000);
    } catch (err) {
      console.error('خطأ في نسخ رابط التوقيع:', err);
      alert('حدث خطأ في نسخ رابط التوقيع');
    }
  };

  const getSignatureStatusBadge = (status) => {
    const statusConfig = {
      draft: { text: 'مسودة', color: 'bg-gray-100 text-gray-700', icon: Clock },
      pending_client_signature: { text: 'بانتظار توقيع العميل', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      pending_lawyer_signature: { text: 'بانتظار توقيع المحامي', color: 'bg-blue-100 text-blue-700', icon: Clock },
      signed: { text: 'موقّع بالكامل', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      archived: { text: 'مؤرشف', color: 'bg-purple-100 text-purple-700', icon: FileCheck },
      cancelled: { text: 'ملغى', color: 'bg-red-100 text-red-700', icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.text}
      </span>
    );
  };

  const getLanguageBadge = (language) => {
    const languageConfig = {
      arabic: { text: 'عربي', color: 'bg-purple-100 text-purple-700' },
      english: { text: 'English', color: 'bg-indigo-100 text-indigo-700' },
      bilingual: { text: 'ثنائي', color: 'bg-teal-100 text-teal-700' },
    };

    const config = languageConfig[language] || languageConfig.arabic;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  // تطبيق الفلاتر المتقدمة
  const filteredContracts = contracts.filter(contract => {
    // البحث
    const matchesSearch = 
      contract.contract_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.field_data.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    // فلتر اللغة
    if (languageFilter && contract.language !== languageFilter) return false;

    // فلتر التاريخ
    if (dateFilter !== 'all') {
      const contractDate = new Date(contract.created_at);
      const now = new Date();
      const daysDiff = Math.floor((now - contractDate) / (1000 * 60 * 60 * 24));

      if (dateFilter === 'today' && daysDiff > 0) return false;
      if (dateFilter === 'week' && daysDiff > 7) return false;
      if (dateFilter === 'month' && daysDiff > 30) return false;
    }

    return true;
  });

  // الترتيب
  const sortedContracts = [...filteredContracts].sort((a, b) => {
    if (sortBy === 'date_desc') {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === 'date_asc') {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (sortBy === 'number') {
      return a.contract_number.localeCompare(b.contract_number);
    }
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContracts = sortedContracts.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">إدارة العقود</h1>
                <p className="text-gray-600">عرض وإدارة جميع العقود</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin/contracts/templates')}
                className="flex items-center gap-2 border-2 border-[#C8A870] text-[#C8A870] px-6 py-3 rounded-lg font-bold hover:bg-[#C8A870]/10"
              >
                <FileText className="w-5 h-5" />
                إدارة القوالب
              </button>
              <button
                onClick={() => navigate('/admin/contracts/generate')}
                className="flex items-center gap-2 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg"
              >
                <Plus className="w-5 h-5" />
                إنشاء عقد جديد
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">إجمالي العقود</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_contracts}</p>
                </div>
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">عقود نشطة</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.active_contracts}</p>
                </div>
                <FileCheck className="w-10 h-10 text-blue-400" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">عقود موقعة</p>
                  <p className="text-2xl font-bold text-green-600">{stats.signed_contracts}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">مسودات</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.draft_contracts}</p>
                </div>
                <Clock className="w-10 h-10 text-gray-400" />
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">هذا الشهر</p>
                  <p className="text-2xl font-bold text-[#C8A870]">{stats.contracts_this_month}</p>
                </div>
                <Calendar className="w-10 h-10 text-[#C8A870]" />
              </div>
            </div>
          </div>
        )}

        {/* Advanced Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#C8A870]" />
            <h3 className="font-bold text-gray-900">الفلاتر والبحث المتقدم</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="بحث برقم العقد أو اسم العميل..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
              >
                <option value="">جميع الحالات</option>
                <option value="draft">مسودة</option>
                <option value="pending_client_signature">بانتظار توقيع العميل</option>
                <option value="pending_lawyer_signature">بانتظار توقيع المحامي</option>
                <option value="signed">موقع</option>
                <option value="active">نشط</option>
                <option value="expired">منتهي</option>
                <option value="terminated">ملغى</option>
              </select>
            </div>

            {/* Language Filter */}
            <div>
              <select
                value={languageFilter}
                onChange={(e) => {
                  setLanguageFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
              >
                <option value="">جميع اللغات</option>
                <option value="arabic">عربي</option>
                <option value="english">English</option>
                <option value="bilingual">ثنائي</option>
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
              >
                <option value="all">جميع التواريخ</option>
                <option value="today">اليوم</option>
                <option value="week">آخر أسبوع</option>
                <option value="month">آخر شهر</option>
              </select>
            </div>
          </div>

          {/* Sort and Results Count */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">الترتيب حسب:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#C8A870] focus:border-transparent"
              >
                <option value="date_desc">الأحدث أولاً</option>
                <option value="date_asc">الأقدم أولاً</option>
                <option value="number">رقم العقد</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-bold text-[#C8A870]">{sortedContracts.length}</span> عقد
            </div>
          </div>
        </div>

        {/* Contracts List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">رقم العقد</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">العميل</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">التاريخ</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">اللغة</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">حالة التوقيع</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedContracts.map(contract => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#C8A870]" />
                        <span className="font-mono font-bold text-gray-900">{contract.contract_number}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900">{contract.field_data.client_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{contract.field_data.contract_date}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getLanguageBadge(contract.language)}
                    </td>
                    <td className="px-6 py-4">
                      {getSignatureStatusBadge(contract.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 flex-wrap">
                        {/* إرسال رابط التوقيع للعميل */}
                        {contract.status === 'pending_client_signature' && (
                          <button
                            onClick={() => sendSignatureLink(contract.id, contract.client_id, contract.contract_number)}
                            disabled={sendingEmail === contract.id}
                            className={`p-2 rounded-lg transition-colors ${
                              sendingEmail === contract.id
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                            }`}
                            title="إرسال رابط التوقيع للعميل"
                          >
                            {sendingEmail === contract.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* عرض سجل التدقيق */}
                        <button
                          onClick={() => navigate(`/admin/contracts/${contract.id}/audit-log`)}
                          className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                          title="سجل التدقيق"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* توقيع المحامي */}
                        {(contract.status === 'pending_lawyer_signature' || contract.status === 'pending_client_signature') && (
                          <button
                            onClick={() => navigate(`/admin/contracts/${contract.id}/sign`)}
                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                            title="توقيع العقد"
                          >
                            <FileSignature className="w-4 h-4" />
                          </button>
                        )}

                        {/* تحميل PDF */}
                        {(contract.language === 'arabic' || contract.language === 'bilingual') && (
                          <button
                            onClick={() => downloadPDF(contract.id, 'ar', contract.contract_number)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                            title="تحميل النسخة العربية"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        {(contract.language === 'english' || contract.language === 'bilingual') && (
                          <button
                            onClick={() => downloadPDF(contract.id, 'en', contract.contract_number)}
                            className="p-2 bg-[#C8A870]/20 text-[#C8A870] rounded-lg hover:bg-[#C8A870]/30 transition-colors"
                            title="تحميل النسخة الإنجليزية"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* روابط المشاركة العامة */}
                        <button
                          onClick={() => getShareLinks(contract.id)}
                          disabled={loadingLinks}
                          className="p-2 bg-teal-100 text-teal-600 rounded-lg hover:bg-teal-200 transition-colors"
                          title="الحصول على روابط مشاركة العقد"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>

                        {/* نسخ رابط التوقيع للعميل */}
                        <button
                          onClick={() => copySignatureLinkForClient(contract.id)}
                          className={`p-2 rounded-lg transition-all ${
                            copiedSignatureLink === contract.id
                              ? 'bg-green-500 text-white'
                              : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                          }`}
                          title="نسخ رابط التوقيع للعميل"
                        >
                          {copiedSignatureLink === contract.id ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {paginatedContracts.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>لا توجد عقود متاحة</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  عرض {startIndex + 1} - {Math.min(endIndex, sortedContracts.length)} من أصل {sortedContracts.length}
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      currentPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    السابق
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      // عرض الصفحات: الأولى، الأخيرة، الحالية، والصفحات المجاورة
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return <span key={page} className="text-gray-400">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      currentPage === totalPages
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    التالي
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal روابط المشاركة */}
      {shareLinksModal && currentContractLinks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <LinkIcon className="w-6 h-6" />
                روابط مشاركة العقد
              </h3>
              <button
                onClick={() => {
                  setShareLinksModal(false);
                  setCurrentContractLinks(null);
                  setCopiedLink(null);
                }}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* معلومات العقد */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">رقم العقد</p>
                <p className="text-lg font-bold text-gray-900 font-mono">
                  {currentContractLinks.contract_number}
                </p>
              </div>

              {/* روابط التحميل */}
              <div className="space-y-4">
                <h4 className="font-bold text-gray-900 flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#C8A870]" />
                  روابط التحميل المباشر
                </h4>

                {/* رابط النسخة العربية */}
                {currentContractLinks.download_links.arabic && (
                  <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-blue-900">النسخة العربية</span>
                      <span className="text-xs px-2 py-1 bg-blue-200 text-blue-800 rounded-full">
                        عربي
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={currentContractLinks.download_links.arabic}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm text-gray-600 font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(currentContractLinks.download_links.arabic, 'arabic')}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          copiedLink === 'arabic'
                            ? 'bg-green-500 text-white'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {copiedLink === 'arabic' ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            تم النسخ
                          </span>
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <a
                      href={currentContractLinks.download_links.arabic}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Eye className="w-4 h-4" />
                      فتح الرابط في نافذة جديدة
                    </a>
                  </div>
                )}

                {/* رابط النسخة الإنجليزية */}
                {currentContractLinks.download_links.english && (
                  <div className="bg-teal-50 p-4 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-teal-900">النسخة الإنجليزية</span>
                      <span className="text-xs px-2 py-1 bg-teal-200 text-teal-800 rounded-full">
                        English
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={currentContractLinks.download_links.english}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-teal-200 rounded-lg text-sm text-gray-600 font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(currentContractLinks.download_links.english, 'english')}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          copiedLink === 'english'
                            ? 'bg-green-500 text-white'
                            : 'bg-teal-600 text-white hover:bg-teal-700'
                        }`}
                      >
                        {copiedLink === 'english' ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            تم النسخ
                          </span>
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <a
                      href={currentContractLinks.download_links.english}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800"
                    >
                      <Eye className="w-4 h-4" />
                      Open link in new window
                    </a>
                  </div>
                )}
              </div>

              {/* رابط التحقق */}
              {currentContractLinks.verification_link && (
                <div className="space-y-2">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-purple-600" />
                    رابط التحقق من العقد
                  </h4>
                  <div className="bg-purple-50 p-4 rounded-lg space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={currentContractLinks.verification_link}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-lg text-sm text-gray-600 font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(currentContractLinks.verification_link, 'verify')}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          copiedLink === 'verify'
                            ? 'bg-green-500 text-white'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {copiedLink === 'verify' ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            تم النسخ
                          </span>
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ملاحظة */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  💡 <strong>ملاحظة:</strong> هذه الروابط عامة ويمكن لأي شخص الوصول إليها. يمكنك مشاركتها مع العميل مباشرة.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 flex justify-end">
              <button
                onClick={() => {
                  setShareLinksModal(false);
                  setCurrentContractLinks(null);
                  setCopiedLink(null);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsListPage;
